import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { getSeriesColor } from './color-palette';
import { ChartPatternDefs } from './ChartPatternDefs';
import { ChartTooltip, TooltipData } from './ChartTooltip';

const MARGIN = { top: 20, right: 120, bottom: 60, left: 60 };
const WIDTH = 700;
const HEIGHT = 400;

export interface CumulativeFlowChartProps {
  readonly data: Array<{ dimensionValue: string; measureValue: number }>;
  readonly dimensionLabel: string;
  readonly measureLabel: string;
  readonly onSegmentClick?: (dimensionValue: string) => void;
}

/**
 * Cumulative Flow Diagram — stacked area chart where each band is a list.
 * Data format: dimensionValue = "date|listTitle", measureValue = cardCount.
 * Lists are stacked from bottom to top in board column order.
 */
export function CumulativeFlowChart({ data, dimensionLabel, measureLabel, onSegmentClick }: CumulativeFlowChartProps): React.ReactElement {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);

  const hideTooltip = useCallback(() => setTooltip(null), []);

  // Parse the encoded data: dimensionValue = "date|listTitle"
  const { dates, lists, matrix } = React.useMemo(() => {
    const dateSet = new Set<string>();
    const listSet = new Set<string>();
    const valueMap = new Map<string, number>();

    for (const d of data) {
      const [date, listTitle] = d.dimensionValue.split('|');
      if (date && listTitle) {
        dateSet.add(date);
        listSet.add(listTitle);
        valueMap.set(`${date}|${listTitle}`, d.measureValue);
      }
    }

    const dates = Array.from(dateSet).sort();
    const lists = Array.from(listSet); // Assume pre-sorted by board column order

    // Build matrix: rows = dates, columns = lists
    const matrix = dates.map((date) =>
      lists.reduce((acc, list) => {
        acc[list] = valueMap.get(`${date}|${list}`) ?? 0;
        return acc;
      }, {} as Record<string, number>),
    );

    return { dates, lists, matrix };
  }, [data]);

  useEffect(() => {
    if (!svgRef.current || dates.length === 0 || lists.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('g.chart-content').remove();

    const innerWidth = WIDTH - MARGIN.left - MARGIN.right;
    const innerHeight = HEIGHT - MARGIN.top - MARGIN.bottom;

    const g = svg.append('g').attr('class', 'chart-content').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

    const xScale = d3.scalePoint().domain(dates).range([0, innerWidth]).padding(0.05);

    // Stack the data
    const stack = d3.stack<Record<string, number>>().keys(lists).order(d3.stackOrderNone).offset(d3.stackOffsetNone);
    const series = stack(matrix);

    const yMax = d3.max(series, (s) => d3.max(s, (d) => d[1])) ?? 0;
    const yScale = d3.scaleLinear().domain([0, yMax]).nice().range([innerHeight, 0]);

    // Area generator
    const area = d3.area<d3.SeriesPoint<Record<string, number>>>()
      .x((_, i) => xScale(dates[i]!) ?? 0)
      .y0((d) => yScale(d[0]))
      .y1((d) => yScale(d[1]));

    // Render stacked areas
    series.forEach((s) => {
      const listTitle = s.key;
      const color = getSeriesColor(listTitle);

      g.append('path')
        .datum(s)
        .attr('fill', color)
        .attr('fill-opacity', 0.7)
        .attr('stroke', color)
        .attr('stroke-width', 1)
        .attr('d', area as any);
    });

    // Axes
    const tickValues = dates.length > 14
      ? dates.filter((_, i) => i % Math.ceil(dates.length / 7) === 0)
      : dates;

    g.append('g').attr('transform', `translate(0,${innerHeight})`).call(d3.axisBottom(xScale).tickValues(tickValues)).selectAll('text').attr('transform', 'rotate(-30)').style('text-anchor', 'end').attr('font-size', '11px');
    g.append('g').call(d3.axisLeft(yScale)).attr('font-size', '12px');

    // Axis labels
    g.append('text').attr('x', innerWidth / 2).attr('y', innerHeight + MARGIN.bottom - 5).attr('text-anchor', 'middle').attr('font-size', '13px').attr('fill', 'currentColor').text(dimensionLabel);
    g.append('text').attr('transform', 'rotate(-90)').attr('x', -innerHeight / 2).attr('y', -MARGIN.left + 15).attr('text-anchor', 'middle').attr('font-size', '13px').attr('fill', 'currentColor').text(measureLabel);

    // Legend (right side)
    const legendG = g.append('g').attr('transform', `translate(${innerWidth + 10}, 0)`);
    lists.forEach((listTitle, i) => {
      const color = getSeriesColor(listTitle);
      legendG.append('rect').attr('x', 0).attr('y', i * 18).attr('width', 12).attr('height', 12).attr('fill', color).attr('rx', 2);
      legendG.append('text').attr('x', 16).attr('y', i * 18 + 10).attr('font-size', '11px').attr('fill', 'currentColor').text(listTitle.length > 12 ? listTitle.slice(0, 12) + '…' : listTitle);
    });
  }, [dates, lists, matrix, dimensionLabel, measureLabel]);

  if (data.length === 0 || dates.length === 0) {
    return <div className="flex h-64 items-center justify-center text-muted-foreground" role="status">No data available</div>;
  }

  return (
    <div ref={containerRef} className="relative" role="img" aria-label={`Cumulative flow diagram: ${dimensionLabel} by ${measureLabel}`}>
      <svg ref={svgRef} viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="h-auto w-full max-w-[700px]" aria-hidden="true"><ChartPatternDefs /></svg>
      <ChartTooltip tooltip={tooltip} />
    </div>
  );
}
